// src/TokenManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './styles/ManagementPages.css';

const AVAILABLE_ENDPOINTS = [
  { label: 'Métaux', value: '/api/metals' },
  { label: 'Produits', value: '/api/products' },
  { label: 'Agences', value: '/api/agences' },
  { label: 'Profil', value: '/api/member/profile' },
  { label: 'Tokens', value: '/api/member/tokens' },
  { label: 'Inscription', value: '/api/signup' },
  { label: 'Devises', value: '/api/currencies' },
  { label: 'Actualités', value: '/api/news' },
];

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseDate(value);
  if (!date) {
    return 'Jamais';
  }
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TokenManager = () => {
  const [tokens, setTokens] = useState([]);
  const [newName, setNewName] = useState('');
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const authToken = localStorage.getItem('token');

  const [editTokenId, setEditTokenId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSelectedEndpoints, setEditSelectedEndpoints] = useState([]);

  const fetchTokens = useCallback(async () => {
    setError('');
    try {
      const response = await axios.get('/api/member/tokens', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setTokens(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la récupération des tokens');
    }
  }, [authToken]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const tokensStats = useMemo(() => {
    const now = new Date();
    const total = tokens.length;
    let lastCreatedAt = null;
    let expiringSoon = 0;
    let unlimited = 0;

    tokens.forEach((token) => {
      const created = parseDate(token.created_at);
      if (created && (!lastCreatedAt || created > lastCreatedAt)) {
        lastCreatedAt = created;
      }

      if (!token.expires_at) {
        unlimited += 1;
      } else {
        const expiry = parseDate(token.expires_at);
        if (expiry) {
          const diffInDays = (expiry - now) / (1000 * 60 * 60 * 24);
          if (diffInDays <= 7) {
            expiringSoon += 1;
          }
        }
      }
    });

    return {
      total,
      unlimited,
      expiringSoon,
      lastCreatedLabel: lastCreatedAt
        ? lastCreatedAt.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })
        : 'Aucun token créé',
    };
  }, [tokens]);

  const handleCheckboxChange = (value, isEdit = false) => {
    if (isEdit) {
      setEditSelectedEndpoints((prev) =>
        prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
      );
    } else {
      setSelectedEndpoints((prev) =>
        prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
      );
    }
  };

  const resetFeedback = () => {
    setFeedback('');
    setError('');
  };

  const handleAddToken = async (event) => {
    event.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      const scopeString = selectedEndpoints.join(',');
      await axios.post(
        '/api/member/tokens',
        { name: newName, scope: scopeString },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setFeedback('Token créé avec succès.');
      setNewName('');
      setSelectedEndpoints([]);
      await fetchTokens();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du token');
    }
    setLoading(false);
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Confirmer la révocation de ce token ?')) {
      return;
    }
    resetFeedback();
    try {
      await axios.delete(`/api/member/tokens/${tokenId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setFeedback('Token révoqué.');
      await fetchTokens();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la révocation du token');
    }
  };

  const handleCopyToken = async (tokenValue) => {
    resetFeedback();
    try {
      await navigator.clipboard.writeText(tokenValue);
      setFeedback('Token copié dans le presse-papiers.');
    } catch (err) {
      setError('Impossible de copier le token automatiquement.');
    }
  };

  const handleEditToken = (token) => {
    setEditTokenId(token.id);
    setEditName(token.name || '');
    setEditSelectedEndpoints(token.scope ? token.scope.split(',') : []);
  };

  const handleCancelEdit = () => {
    setEditTokenId(null);
    setEditName('');
    setEditSelectedEndpoints([]);
  };

  const handleSaveEdit = async (tokenId) => {
    resetFeedback();
    setLoading(true);
    try {
      const scopeString = editSelectedEndpoints.join(',');
      await axios.put(
        `/api/member/tokens/${tokenId}`,
        { name: editName, scope: scopeString },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setFeedback('Token mis à jour.');
      await fetchTokens();
      handleCancelEdit();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour du token');
    }
    setLoading(false);
  };

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">🔑</span>
              Gestion des tokens API
            </h1>
            <p className="management-subtitle">
              Créez, révisez et contrôlez les accès API de vos intégrations. Chaque token est
              scellé par un périmètre précis afin de protéger les flux sensibles.
            </p>
            <div className="management-meta">
              <span className="management-badge">Sécurité renforcée</span>
              <span className="management-badge">Scopes granulaires</span>
            </div>
          </div>
          <div className="management-header__actions">
            <button
              type="button"
              className="mgmt-btn mgmt-btn--outline"
              onClick={fetchTokens}
              disabled={loading}
            >
              Actualiser
            </button>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Tokens actifs</span>
            <span className="management-stat-value">{tokensStats.total}</span>
            <span className="management-stat-meta">Total actuellement disponible</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Sans expiration</span>
            <span className="management-stat-value">{tokensStats.unlimited}</span>
            <span className="management-stat-meta">
              Idéal pour les intégrations long terme
            </span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Expiration &lt; 7 jours</span>
            <span className="management-stat-value">{tokensStats.expiringSoon}</span>
            <span className="management-stat-meta">À renouveler rapidement</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Dernier créé</span>
            <span className="management-stat-value">{tokensStats.lastCreatedLabel}</span>
            <span className="management-stat-meta">Historique de provisionnement</span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}
        {feedback && (
          <div className="management-alert success">
            <span>✅</span>
            {feedback}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Créer un nouveau token</h2>
            <p className="management-section__subtitle">
              Nommez votre accès et choisissez les endpoints autorisés. Vous pouvez révoquer ou
              restreindre les droits à tout moment.
            </p>
          </div>
          <form className="management-form" onSubmit={handleAddToken}>
            <div className="management-form__grid">
              <div className="management-form__field">
                <label htmlFor="tokenName" className="management-form__label">
                  Nom du token
                </label>
                <input
                  id="tokenName"
                  className="mgmt-input"
                  type="text"
                  placeholder="Ex. Tableau de bord BI"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  required
                />
              </div>
              <div className="management-form__field">
                <label className="management-form__label">Endpoints autorisés</label>
                <div className="management-checkbox-grid">
                  {AVAILABLE_ENDPOINTS.map((endpoint) => (
                    <label key={endpoint.value} className="management-checkbox">
                      <input
                        type="checkbox"
                        value={endpoint.value}
                        checked={selectedEndpoints.includes(endpoint.value)}
                        onChange={() => handleCheckboxChange(endpoint.value)}
                      />
                      {endpoint.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="management-actions">
              <button
                type="submit"
                className="mgmt-btn mgmt-btn--primary"
                disabled={loading}
              >
                {loading ? 'Création…' : 'Ajouter le token'}
              </button>
              <button
                type="button"
                className="mgmt-btn mgmt-btn--ghost"
                onClick={() => {
                  setNewName('');
                  setSelectedEndpoints([]);
                }}
                disabled={loading}
              >
                Réinitialiser
              </button>
            </div>
          </form>
        </section>

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Tokens existants</h2>
            <p className="management-section__subtitle">
              Visualisez l’ensemble des clés délivrées, leur champ d’action et leur durée de
              validité. Utilisez les actions rapides pour mettre à jour ou révoquer un accès.
            </p>
          </div>

          {tokens.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">🗝️</span>
              <span className="management-empty-state__title">Aucun token pour le moment</span>
              <span className="management-empty-state__subtitle">
                Créez votre premier accès API afin de connecter vos dashboards ou intégrations
                externes en toute sécurité.
              </span>
            </div>
          ) : (
            <>
              <div className="management-table-scroll">
                <table className="management-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nom & périmètre</th>
                      <th>Endpoints</th>
                      <th>Token</th>
                      <th>Créé le</th>
                      <th>Expiration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.map((token) => {
                      const isEditing = editTokenId === token.id;
                      const scopes = token.scope ? token.scope.split(',') : [];

                      return (
                        <tr key={token.id}>
                          <td>
                            <strong>#{token.id}</strong>
                          </td>
                          <td>
                            <div className="management-inline-field">
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="mgmt-input mgmt-input--small"
                                  value={editName}
                                  onChange={(event) => setEditName(event.target.value)}
                                  placeholder="Nom du token"
                                />
                              ) : (
                                <span>{token.name || 'Sans nom'}</span>
                              )}
                              <span className="management-tag">{scopes.length} endpoint(s)</span>
                            </div>
                          </td>
                          <td>
                            {isEditing ? (
                              <div className="management-checkbox-grid">
                                {AVAILABLE_ENDPOINTS.map((endpoint) => (
                                  <label key={endpoint.value} className="management-checkbox">
                                    <input
                                      type="checkbox"
                                      value={endpoint.value}
                                      checked={editSelectedEndpoints.includes(endpoint.value)}
                                      onChange={() => handleCheckboxChange(endpoint.value, true)}
                                    />
                                    {endpoint.label}
                                  </label>
                                ))}
                              </div>
                            ) : scopes.length > 0 ? (
                              <div className="management-inline-actions">
                                {scopes.map((scope) => (
                                  <span key={scope} className="management-tag management-tag--success">
                                    {scope}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="management-tag management-tag--warning">
                                Aucun scope
                              </span>
                            )}
                          </td>
                          <td>
                            <code title={token.token}>
                              {token.token.length > 24 ? `${token.token.slice(0, 24)}…` : token.token}
                            </code>
                          </td>
                          <td>{formatDateTime(token.created_at)}</td>
                          <td>{formatDateTime(token.expires_at)}</td>
                          <td>
                            {isEditing ? (
                              <div className="management-actions">
                                <button
                                  type="button"
                                  className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                                  onClick={() => handleSaveEdit(token.id)}
                                  disabled={loading}
                                >
                                  Enregistrer
                                </button>
                                <button
                                  type="button"
                                  className="mgmt-btn mgmt-btn--ghost mgmt-btn--small"
                                  onClick={handleCancelEdit}
                                  disabled={loading}
                                >
                                  Annuler
                                </button>
                              </div>
                            ) : (
                              <div className="management-actions">
                                <button
                                  type="button"
                                  className="mgmt-btn mgmt-btn--outline mgmt-btn--small"
                                  onClick={() => handleCopyToken(token.token)}
                                >
                                  Copier
                                </button>
                                <button
                                  type="button"
                                  className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                                  onClick={() => handleEditToken(token)}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="mgmt-btn mgmt-btn--danger mgmt-btn--small"
                                  onClick={() => handleRevokeToken(token.id)}
                                  disabled={loading}
                                >
                                  Révoquer
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="management-summary">
                <span>
                  <strong>{tokens.length}</strong> token(s) configuré(s)
                </span>
                <span>
                  {tokensStats.expiringSoon > 0
                    ? `${tokensStats.expiringSoon} expirent d’ici 7 jours`
                    : 'Aucune expiration rapprochée'}
                </span>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default TokenManager;